import React, { useMemo, useState } from "react";
import { PDFDocument } from "pdf-lib";
import Dropzone from "./Dropzone";
import FileListReorder from "./FileListReorder";

function baseName(filename = "document.pdf") {
  return filename.replace(/\.[^/.]+$/, "");
}

export default function MergePdf() {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null); // { url, name }
  const [error, setError] = useState("");

  const canMerge = useMemo(
    () => files.length >= 2 && !loading,
    [files, loading]
  );

  const cleanup = () => {
    if (result?.url) URL.revokeObjectURL(result.url);
    setResult(null);
  };

  const onPick = (picked) => {
    cleanup();
    setError("");

    const arr = (picked || []).filter(
      (f) =>
        f.type === "application/pdf" || f.name.toLowerCase().endsWith(".pdf")
    );

    if (!arr.length) return;

    setFiles((prev) => {
      // clé simple anti-doublons
      const key = (f) => `${f.name}__${f.size}__${f.lastModified}`;
      const existing = new Set(prev.map(key));

      const toAdd = arr.filter((f) => !existing.has(key(f)));

      return [...prev, ...toAdd];
    });
  };

  const onReorder = (from, to) => {
    cleanup();
    setError("");
    setFiles((prev) => {
      const next = [...prev];
      if (to < 0 || to >= next.length) return prev;
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return next;
    });
  };

  const onRemove = (index) => {
    cleanup();
    setError("");
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const merge = async () => {
    if (files.length < 2) return;
    setLoading(true);
    setError("");
    cleanup();

    try {
      const mergedPdf = await PDFDocument.create();

      for (const f of files) {
        const bytes = await f.arrayBuffer();
        const doc = await PDFDocument.load(bytes);
        const pages = await mergedPdf.copyPages(doc, doc.getPageIndices());
        pages.forEach((p) => mergedPdf.addPage(p));
      }

      const mergedBytes = await mergedPdf.save();
      const blob = new Blob([mergedBytes], { type: "application/pdf" });

      const outName =
        files.length === 2
          ? `${baseName(files[0].name)}+${baseName(files[1].name)}-fusion.pdf`
          : `pdf-fusion-${files.length}-fichiers.pdf`;

      const url = URL.createObjectURL(blob);
      setResult({ url, name: outName });
    } catch (e) {
      console.error(e);
      setError("Impossible de fusionner ces PDFs (corrompu/protégé).");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section style={{ padding: 16 }}>
      <Dropzone
        accept="application/pdf,.pdf"
        multiple
        disabled={loading}
        acceptText="Déposez vos PDF ici"
        helperText="Glissez-déposez plusieurs PDF ou cliquez pour choisir"
        onFiles={onPick}
      />

      {files.length > 0 && (
        <>
          <p style={{ textAlign: "center", color: "#666", marginTop: 8 }}>
            <b>{files.length}</b> fichier(s) sélectionné(s) — glisse pour
            changer l’ordre.
          </p>

          <FileListReorder
            files={files}
            onReorder={onReorder}
            onRemove={onRemove}
          />
        </>
      )}

      <div
        style={{
          display: "flex",
          justifyContent: "center",
          gap: 12,
          marginTop: 12,
          flexWrap: "wrap",
        }}
      >
        <button onClick={merge} disabled={!canMerge} style={{ minWidth: 220 }}>
          {loading ? "Fusion..." : "Fusionner en un seul PDF"}
        </button>

        {files.length > 0 && (
          <button
            onClick={() => {
              cleanup();
              setFiles([]);
              setError("");
            }}
            disabled={loading}
            style={{ minWidth: 160 }}
          >
            Réinitialiser
          </button>
        )}
      </div>

      {error && (
        <p style={{ textAlign: "center", marginTop: 12, color: "#b00020" }}>
          {error}
        </p>
      )}

      {result?.url && (
        <div style={{ textAlign: "center", marginTop: 16 }}>
          <a href={result.url} download={result.name}>
            Télécharger le PDF fusionné
          </a>
        </div>
      )}
    </section>
  );
}
